// Cloudflare Turnstile for the public forms (contact, demo request, onboarding kit alert).
//
// Optional and inert until VITE_TURNSTILE_SITE_KEY is set: with no key nothing is loaded, no widget
// renders and getToken() resolves to "" at once, so every form behaves exactly as before. The server
// side (supabase/functions/_shared/formGuard.ts) only verifies tokens once TURNSTILE_SECRET_KEY is set.
// If Cloudflare's script cannot load (blocker, offline) the token is "" and the form still submits.
//
// Usage:   const ts = useTurnstile();   {ts.enabled && <div ref={ts.ref} />}   ...   turnstile_token: await ts.getToken()
// Tokens are single use: getToken() resets the widget so the next submit gets a fresh one.

import { useCallback, useRef } from "react";
import { createTurnstileController, type TurnstileApi, type TurnstileController } from "./turnstileController";

export const TURNSTILE_SITE_KEY: string = ((import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? "").trim();

declare global {
  interface Window { turnstile?: TurnstileApi }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const LOAD_TIMEOUT_MS = 8000;
let loader: Promise<TurnstileApi | null> | null = null;

/** Loads Cloudflare's script once. Resolves null (never rejects) when there is no key or it fails to load. */
export function loadTurnstile(): Promise<TurnstileApi | null> {
  if (!TURNSTILE_SITE_KEY || typeof document === "undefined") return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!loader) {
    loader = new Promise<TurnstileApi | null>((resolve) => {
      const done = (api: TurnstileApi | null) => { clearTimeout(timer); if (!api) loader = null; resolve(api); };
      const timer = setTimeout(() => done(null), LOAD_TIMEOUT_MS);
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => done(window.turnstile ?? null);
      s.onerror = () => done(null);
      document.head.appendChild(s);
    });
  }
  return loader;
}

export function useTurnstile(waitMs = 4000) {
  const controller = useRef<TurnstileController | null>(null);
  if (!controller.current) controller.current = createTurnstileController(TURNSTILE_SITE_KEY, loadTurnstile, waitMs);
  const c = controller.current;

  // Callback ref: the widget renders when the container mounts (it may sit in a wizard step that appears
  // later) and is removed when it unmounts.
  const ref = useCallback((el: HTMLDivElement | null) => { if (el) c.attach(el); else c.detach(); }, [c]);
  const getToken = useCallback(() => c.getToken(), [c]);

  return { ref, getToken, enabled: TURNSTILE_SITE_KEY !== "" };
}
